import { useEffect, useRef, useState } from "react";
import { genreOptions, regionOptions, sortOptions, yearOptions, type SortValue } from "@/lib/content";

interface FilterBarProps {
  genre: string;
  year: string;
  region: string;
  sort: SortValue;
  resultCount: number;
  onGenreChange: (value: string) => void;
  onYearChange: (value: string) => void;
  onRegionChange: (value: string) => void;
  onSortChange: (value: SortValue) => void;
  onReset: () => void;
}

function FilterGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-4">
      <span className="shrink-0 pt-1.5 text-xs font-semibold uppercase tracking-wider text-foreground-500 sm:w-12">
        {label}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap md:text-sm ${
              value === option
                ? "bg-primary-900 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900"
            }`}
            aria-pressed={value === option}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({
  genre,
  year,
  region,
  sort,
  resultCount,
  onGenreChange,
  onYearChange,
  onRegionChange,
  onSortChange,
  onReset,
}: FilterBarProps) {
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement | null>(null);
  const currentSort = sortOptions.find((option) => option.value === sort) ?? sortOptions[0];

  useEffect(() => {
    if (!sortOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [sortOpen]);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
      <div className="flex flex-col gap-5">
        <FilterGroup label="类型" options={genreOptions} value={genre} onChange={onGenreChange} />
        <FilterGroup label="年份" options={yearOptions} value={year} onChange={onYearChange} />
        <FilterGroup label="地区" options={regionOptions} value={region} onChange={onRegionChange} />
      </div>

      <div className="mt-5 flex flex-col gap-3 border-t border-background-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-foreground-500">
          共找到 <span className="font-label font-bold text-primary-800">{resultCount}</span> 部符合条件的影视剧
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-1.5 rounded-md px-3 py-2 text-xs text-foreground-500 transition-colors whitespace-nowrap hover:text-primary-700 md:text-sm"
          >
            <i className="ri-refresh-line text-sm" aria-hidden="true" />
            重置筛选
          </button>
          <div className="relative" ref={sortRef}>
            <button
              type="button"
              onClick={() => setSortOpen((prev) => !prev)}
              className="flex items-center gap-2 rounded-md border border-background-300 px-3 py-2 text-xs font-medium text-foreground-700 transition-colors whitespace-nowrap hover:border-primary-300 md:text-sm"
              aria-haspopup="listbox"
              aria-expanded={sortOpen}
            >
              <i className="ri-sort-desc text-sm" aria-hidden="true" />
              {currentSort.label}
              <i className={`ri-arrow-down-s-line text-base transition-transform ${sortOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {sortOpen && (
              <ul
                className="absolute right-0 z-20 mt-1 w-40 animate-slide-down overflow-hidden rounded-md border border-background-200 bg-background-50 py-1"
                role="listbox"
              >
                {sortOptions.map((option) => (
                  <li key={option.value}>
                    <button
                      type="button"
                      onClick={() => {
                        onSortChange(option.value);
                        setSortOpen(false);
                      }}
                      className={`flex w-full items-center justify-between px-3 py-2 text-left text-xs transition-colors hover:bg-background-100 md:text-sm ${
                        option.value === sort ? "font-semibold text-primary-800" : "text-foreground-600"
                      }`}
                      role="option"
                      aria-selected={option.value === sort}
                    >
                      {option.label}
                      {option.value === sort && <i className="ri-check-line text-sm text-accent-500" aria-hidden="true" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}